import { Component, OnInit } from '@angular/core';
import { ResultModalRedeem, StatusBag, TypeLoading, RedeemMethod, BENEFIT_TYPE } from '@app/enums';
import { Passenger, Reservation } from '@app/models/micro';
import { LoadingService } from '@app/services/loading.service';
import { ApiAncillaries } from '@app/services/micro/ancillaries.service';
import { GlobalsService } from '@app/services/micro/globals.service';
import {
  classCssTiers,
  COBRAND_REDEN_BENEFIT_TEXTS,
  detectCobrand,
} from '@app/utils/constants/redentions';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { BENEFIT_ICON_CONFIG } from './benefit-offer-modal.images';

@Component({
  selector: 'app-benefit-offer-modal',
  templateUrl: './benefit-offer-modal.component.html',
  styleUrls: ['./benefit-offer-modal.component.scss'],
  standalone: false,
})
export class BenefitOfferModalComponent implements OnInit {
  showLoading = false;
  submitRedeem = false;
  iconSrc = '';

  passenger: Passenger;
  reservation: Reservation;
  selectedMethod: RedeemMethod = 'cobrand';
  onlyCarryOn = false;

  constructor(
    public readonly modal: NgbActiveModal,
    private readonly ancillaries: ApiAncillaries,
    private readonly globalService: GlobalsService,
    private readonly loadingService: LoadingService
  ) {}

  get classTier() {
    return classCssTiers[this.passenger.benefits?.tier] || '';
  }

  get existsAncillaries() {
    return this.passenger.bagOfferStack?.length > 0;
  }

  get isMainLight(): boolean {
    return this.reservation?.flightSummary?.fareFamily === 'MAIN_LIGHT';
  }

  get benefitSubtitleKey(): string {
    if (this.onlyCarryOn) {
      return 'modalbagsredemption.benefitSubtitleCarryOnOnly';
    }
    const hasCarryOn = this.passenger.bagStackCarryon?.some(
      (b) => (b.status === StatusBag.INCLUDE || b.status === StatusBag.PAID || b.status === StatusBag.TO_PAY) && !b.used
    );
    return this.isMainLight && !hasCarryOn
      ? 'modalbagsredemption.benefitSubtitle'
      : 'modalbagsredemption.benefitSubtitleCheckedOnly';
  }

  ngOnInit() {
    this.settingBenefitIcon();
    this.loadingService.loadings[TypeLoading.COMPONENT_LOADING].loading$.subscribe(
      (loading: boolean) => {
        if (loading) {
          this.showLoading = true;
        }
      }
    );
  }

  settingBenefitTitle(text: string): string {
    const cobrandCode = this.passenger.benefits.cobrandCode;
    const cobrandPatternType = detectCobrand(cobrandCode);
    const cobrandPatternText = COBRAND_REDEN_BENEFIT_TEXTS[cobrandPatternType];
    const updatedText = text.replace('[cobrandPattern]', cobrandPatternText);
    return updatedText;
  }

  redeemBag(passenger: Passenger) {
    this.submitRedeem = true;

    if (this.onlyCarryOn) {
      this.redeemCarryOnOnly(passenger);
      return;
    }

    const extraBagsChecked = passenger.bagOfferStack?.find(
      (bag) => bag.order === passenger.bagStack.length
    );
    const hasCarryOnIncluded = passenger.bagStackCarryon?.some(
      (bag) => (bag.status === StatusBag.INCLUDE || bag.status === StatusBag.PAID) && !bag.used && !bag.isRedemptionCobrand && !bag.isRedemptionTier && !bag.isRedemptionFF
    );
    const carryOnRedeemable = !hasCarryOnIncluded
      ? passenger.bagStackCarryon?.find((b) => b.status === StatusBag.REDEEMED)
      : null;
    const extraBagsCarryOn = carryOnRedeemable
      ? passenger.bagOfferCarryOnStack?.[0] || carryOnRedeemable
      : null;

    const ancillariesToRedeem =
      extraBagsCarryOn && extraBagsChecked
        ? [extraBagsChecked, extraBagsCarryOn]
        : extraBagsChecked;

    this.ancillaries
      .redeemAncillaries(this.reservation, passenger, ancillariesToRedeem, this.selectedMethod)
      .subscribe({
        next: (res) => {
          const bag = passenger.bagStack.find((b) => b.status === StatusBag.REDEEMED);
          const bagRedeemed = res?.travelerInfo[0]?.ancillaries?.[0]?.ancillary;
          if (bag) {
            if (bagRedeemed) Object.assign(bag, bagRedeemed);
            bag.status = StatusBag.REDEEMED_APPLIED;
            bag.isRedemptionFF = false;
            bag.isRedemptionTier = this.selectedMethod === BENEFIT_TYPE.TIER;
            bag.isRedemptionCobrand = this.selectedMethod === BENEFIT_TYPE.COBRAND;
            bag.isRedemptionCorporate = this.selectedMethod === BENEFIT_TYPE.CORPORATE;
            bag.selected = true;
          }
          if (carryOnRedeemable) {
            const carryOnRedeemed = res?.travelerInfo[0]?.ancillaries?.[1]?.ancillary;
            if (carryOnRedeemed) Object.assign(carryOnRedeemable, carryOnRedeemed);
            carryOnRedeemable.status = StatusBag.REDEEMED_APPLIED;
            carryOnRedeemable.isRedemptionCobrand = true;
            carryOnRedeemable.selected = true;
            // Remover carry-on con TO_PAY ya que fue reemplazado por la redención
            if (passenger.bagStackCarryon) {
              passenger.bagStackCarryon = passenger.bagStackCarryon.filter(
                (b) => b.status !== StatusBag.TO_PAY
              );
            }
            // Resetear bagOfferCarryOnStack para reflejar que ya no hay carry-on pendiente
            if (passenger.bagOfferCarryOnStack) {
              passenger.bagOfferCarryOnStack.forEach((offerBag) => {
                if (offerBag.status === StatusBag.TO_PAY) {
                  offerBag.status = StatusBag.NONE;
                  offerBag.selected = false;
                }
              });
            }
          }
          this.globalService.setPassenger(passenger);
          this.modal.close({
            result: ResultModalRedeem.SUCCESS,
            selectedMethod: this.selectedMethod,
          });
          this.submitRedeem = false;
        },
        error: () => {
          this.submitRedeem = false;
          this.modal.close(ResultModalRedeem.ERROR);
        },
      });
  }

  private redeemCarryOnOnly(passenger: Passenger) {
    const carryOnRedeemable = passenger.bagStackCarryon?.find(
      (bag) => bag.status === StatusBag.REDEEMED
    );
    const extraCarryOn = passenger.bagOfferCarryOnStack?.[0] || carryOnRedeemable;

    this.ancillaries
      .redeemAncillaries(this.reservation, passenger, extraCarryOn, this.selectedMethod)
      .subscribe({
        next: (res) => {
          if (carryOnRedeemable) {
            const bagRedeemed = res?.travelerInfo[0]?.ancillaries?.[0]?.ancillary;
            if (bagRedeemed) Object.assign(carryOnRedeemable, bagRedeemed);
            carryOnRedeemable.status = StatusBag.REDEEMED_APPLIED;
            carryOnRedeemable.isRedemptionCobrand = true;
            carryOnRedeemable.selected = true;
            // Remover carry-on con TO_PAY ya que fue reemplazado por la redención
            if (passenger.bagStackCarryon) {
              passenger.bagStackCarryon = passenger.bagStackCarryon.filter(
                (b) => b.status !== StatusBag.TO_PAY
              );
            }
            // Resetear bagOfferCarryOnStack para reflejar que ya no hay carry-on pendiente
            if (passenger.bagOfferCarryOnStack) {
              passenger.bagOfferCarryOnStack.forEach((offerBag) => {
                if (offerBag.status === StatusBag.TO_PAY) {
                  offerBag.status = StatusBag.NONE;
                  offerBag.selected = false;
                }
              });
            }
          }
          this.globalService.setPassenger(passenger);
          this.modal.close({
            result: ResultModalRedeem.SUCCESS,
            selectedMethod: this.selectedMethod,
          });
          this.submitRedeem = false;
        },
        error: () => {
          this.submitRedeem = false;
          this.modal.close(ResultModalRedeem.ERROR);
        },
      });
  }

  onClose() {
    this.modal.close(ResultModalRedeem.NO_REDEEM);
  }

  onRedeem() {
    if (this.submitRedeem) return;
    this.redeemBag(this.passenger);
  }

  private settingBenefitIcon() {
    const code = this.passenger.benefits.cobrandCode;
    const selectedConfig = BENEFIT_ICON_CONFIG.find((e) => e.code === code);
    this.iconSrc = selectedConfig.icon;
  }
}
